<template>
  <component
    :is="tag"
    ref="root"
    class="ak-masthead__nav-item"
    :to="to"
    v-bind="attrs"
  >
    <slot></slot>
  </component>
</template>

<script>
import { defineComponent, onBeforeUnmount, onMounted, ref, inject } from 'vue'

const clickableWhiteList = ['div', 'span']

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-masthead-item></ak-masthead-item>
   */
  name: 'AkMastheadItem',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Boolean}
     * @description Sets the active state of the masthead item
     */
    active: Boolean,

    /**
     * @type {String}
     * @description This allows us to define a route to send the user to
     * when the "tag" property is set to "router-link"
     */
    to: {
      default: undefined,
      type: String,
    },

    /**
     * @type {String}
     * @description The tag name for the root element of the component
     * this allows us to use an anchor or a router-link
     */
    tag: {
      type: String,
      default: 'a',
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Object} options.attrs - The attributes method for the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { attrs }) {
    const root = ref(null)
    const parent = inject('akMasthead')

    /**
     * @param {Event} event
     * @description Keypress event that is bound to the document
     */
    const keyPress = (event) => {
      if (event.keyCode === 27) {
        parent.closeMenu()
      }
    }

    /**
     * @param {Event} event
     * @description Close parent if clicked outside.
     */
    const handleClickEvent = (event) => {
      const isOnWhiteList = clickableWhiteList.some(
        (item) => item === event.target.localName
      )

      if (!isOnWhiteList) {
        parent.closeMenu()
      }
    }

    onMounted(() => {
      /* istanbul ignore else */
      if (typeof window !== 'undefined') {
        root.value.addEventListener('click', handleClickEvent)
        document.addEventListener('keyup', keyPress)
      }
    })

    onBeforeUnmount(() => {
      /* istanbul ignore else */
      if (typeof window !== 'undefined') {
        root.value.removeEventListener('click', handleClickEvent)
        document.removeEventListener('keyup', keyPress)
      }
    })

    return {
      keyPress,
      handleClickEvent,
      root,
      attrs,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-masthead__nav-item {
  display: flex;
  align-items: center;
  height: 100%;
  padding-left: $spacing-tiny;
  padding-right: $spacing-tiny;
  transition: $transition;
  font-weight: bold;
  font-size: $font-size-smaller;
  @media screen and #{$mq-nav-large-min} {
    font-size: $font-size-small;
    padding-left: $spacing-small;
    padding-right: $spacing-small;
  }

  &,
  a {
    color: #fff;
    &:hover,
    &:focus {
      color: #fff;
    }
  }
  &:hover,
  &:focus,
  &.is-open {
    background-color: $color-grey-darker;
  }
}
</style>
